import { Link } from "react-router-dom";
import { Palette, Clock, ArrowLeft, ArrowRight, Check, Sparkles, MousePointer, Download, Printer } from "lucide-react";
import { Button } from "../../components/ui/button";

const ArticleCreateColoringPagesWithAI = () => {
  const steps = [
    {
      num: 1,
      title: "Sign Up for ColorWonder (Free)",
      description: "Create your free account in seconds. No credit card required.",
      details: [
        "Go to ColorWonder.app",
        "Click 'Sign Up Free'",
        "Use Google sign-in or create an account with email",
        "You'll get 10 free pages per month to start"
      ]
    },
    {
      num: 2,
      title: "Write Your Prompt",
      description: "Describe what you want to see on your coloring page.",
      details: [
        "Be specific: 'cute baby elephant playing with butterflies'",
        "Add setting: 'in a sunny meadow'",
        "Specify style: 'cartoon style' or 'realistic'",
        "Mention age group for appropriate complexity"
      ]
    },
    {
      num: 3,
      title: "Choose Your Settings",
      description: "Customize the output to match your needs.",
      details: [
        "Select age group (toddlers, kids, or teens)",
        "Choose art style (cute, cartoon, realistic, fantasy)",
        "Pick number of pages (1-10)",
        "Plus/Pro users: Select batch variations"
      ]
    },
    {
      num: 4,
      title: "Generate Your Pages",
      description: "Click generate and let AI create your coloring pages.",
      details: [
        "Click the 'Generate' button",
        "Wait 30-60 seconds per page",
        "Preview your results",
        "Regenerate any page you don't love"
      ]
    },
    {
      num: 5,
      title: "Download & Print",
      description: "Get your print-ready coloring pages.",
      details: [
        "Download as high-quality PDF",
        "Pages are A4 optimized",
        "Print on standard paper",
        "Save to your library for later"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/blog" className="text-slate-600 hover:text-slate-900 font-medium">
                Blog
              </Link>
              <Button
                onClick={() => window.location.href = "/signup"}
                className="h-10 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold"
              >
                Try Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Article Content */}
      <article className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>

          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center gap-3 text-sm text-slate-500 mb-4">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">Tutorials</span>
              <span>•</span>
              <span>Jan 2026</span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                12 min read
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              How to Create Coloring Pages with AI (Free Tutorial)
            </h1>
            
            <p className="text-xl text-slate-600 leading-relaxed">
              Learn how to create beautiful, printable coloring pages using AI – no design skills required! This step-by-step guide shows you exactly how to go from idea to printed page in under 5 minutes.
            </p>
          </header>

          {/* What You'll Learn */}
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-12">
            <h2 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
              <Check className="w-5 h-5" />
              What You'll Learn
            </h2>
            <ul className="space-y-2 text-green-700">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 flex-shrink-0" />
                How to write effective prompts for coloring pages
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 flex-shrink-0" />
                Step-by-step process to generate pages with ColorWonder
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 flex-shrink-0" />
                Tips for getting the best quality results
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 flex-shrink-0" />
                How to download and print your creations
              </li>
            </ul>
          </div>

          {/* Table of Contents */}
          <div className="bg-slate-50 rounded-2xl p-6 mb-12">
            <h2 className="font-semibold text-slate-800 mb-4">📋 Table of Contents</h2>
            <ul className="space-y-2 text-slate-600">
              <li><a href="#what-is-ai" className="hover:text-violet-600">What is AI Coloring Page Generation?</a></li>
              <li><a href="#what-you-need" className="hover:text-violet-600">What You Need to Get Started</a></li>
              <li><a href="#step-by-step" className="hover:text-violet-600">Step-by-Step Tutorial</a></li>
              <li><a href="#prompt-tips" className="hover:text-violet-600">Pro Tips for Better Prompts</a></li>
              <li><a href="#examples" className="hover:text-violet-600">Example Prompts & Results</a></li>
              <li><a href="#faq" className="hover:text-violet-600">FAQs</a></li>
            </ul>
          </div>

          {/* What is AI Generation */}
          <section id="what-is-ai" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              What is AI Coloring Page Generation?
            </h2>
            <p className="text-slate-600 mb-4">
              AI coloring page generators use artificial intelligence to turn text descriptions into printable line art. Instead of searching through thousands of generic coloring pages online, you can create <strong>exactly what you want</strong> by simply describing it.
            </p>
            <div className="bg-violet-50 rounded-xl p-4 mb-4">
              <p className="text-slate-700">
                <strong>Example:</strong> Type "friendly dinosaur reading a book in a cozy library" and get a unique coloring page that doesn't exist anywhere else!
              </p>
            </div>
            <p className="text-slate-600">
              This technology is perfect for parents who want personalized pages for their kids, teachers creating custom educational materials, or anyone tired of the same old coloring book designs.
            </p>
          </section>

          {/* What You Need */}
          <section id="what-you-need" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              What You Need to Get Started
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-slate-100 p-5 text-center">
                <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <MousePointer className="w-6 h-6 text-violet-600" />
                </div>
                <h4 className="font-semibold text-slate-800 mb-1">A Computer or Phone</h4>
                <p className="text-slate-500 text-sm">Works on any device with a browser</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-100 p-5 text-center">
                <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="w-6 h-6 text-violet-600" />
                </div>
                <h4 className="font-semibold text-slate-800 mb-1">ColorWonder Account</h4>
                <p className="text-slate-500 text-sm">Free to sign up, no card required</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-100 p-5 text-center">
                <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Printer className="w-6 h-6 text-violet-600" />
                </div>
                <h4 className="font-semibold text-slate-800 mb-1">Printer (Optional)</h4>
                <p className="text-slate-500 text-sm">To print your finished pages</p>
              </div>
            </div>
          </section>

          {/* Step by Step */}
          <section id="step-by-step" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Step-by-Step Tutorial
            </h2>
            
            <div className="space-y-6">
              {steps.map((step, i) => (
                <div key={i} className="relative pl-12">
                  {/* Step Number */}
                  <div className="absolute left-0 top-0 w-8 h-8 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                    {step.num}
                  </div>
                  
                  {/* Connector Line */}
                  {i < steps.length - 1 && (
                    <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-violet-200 -translate-x-1/2" />
                  )}
                  
                  {/* Content */}
                  <div className="bg-white rounded-2xl border border-slate-100 p-6">
                    <h3 className="text-xl font-bold text-slate-800 mb-2">{step.title}</h3>
                    <p className="text-slate-600 mb-4">{step.description}</p>
                    <ul className="space-y-2">
                      {step.details.map((detail, j) => (
                        <li key={j} className="flex items-start gap-2 text-slate-600">
                          <Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            {/* Try It Now CTA */}
            <div className="mt-8 p-6 bg-gradient-to-r from-violet-100 to-pink-100 rounded-2xl text-center">
              <p className="text-slate-700 mb-4">Ready to try it yourself? Create your first coloring page in under 2 minutes!</p>
              <Button
                onClick={() => window.location.href = "/signup"}
                className="h-12 px-8 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold"
              >
                Start Creating Free →
              </Button>
            </div>
          </section>

          {/* Prompt Tips */}
          <section id="prompt-tips" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Pro Tips for Better Prompts
            </h2>
            <p className="text-slate-600 mb-6">
              The quality of your coloring page depends largely on your prompt. Here's how to write prompts that get amazing results:
            </p>

            <div className="space-y-4">
              {[
                {
                  bad: "dog",
                  good: "happy golden retriever puppy playing with a ball in the park",
                  tip: "Add details! Describe the subject, action, and setting."
                },
                {
                  bad: "princess",
                  good: "young princess with long flowing hair reading a book in a garden filled with roses",
                  tip: "Include character details and environment."
                },
                {
                  bad: "dinosaur",
                  good: "friendly cartoon T-Rex wearing a chef hat and baking cookies",
                  tip: "Make it unique! Add unexpected elements for fun pages."
                },
                {
                  bad: "car",
                  good: "race car zooming around a track with cheering crowd, simple cartoon style for ages 4-6",
                  tip: "Specify the style and target age for appropriate complexity."
                }
              ].map((example, i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-100 p-5">
                  <div className="grid md:grid-cols-2 gap-4 mb-3">
                    <div className="bg-red-50 rounded-lg p-3">
                      <span className="text-xs font-semibold text-red-600 block mb-1">❌ TOO VAGUE</span>
                      <p className="text-slate-700">{example.bad}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3">
                      <span className="text-xs font-semibold text-green-600 block mb-1">✓ BETTER</span>
                      <p className="text-slate-700">{example.good}</p>
                    </div>
                  </div>
                  <p className="text-slate-500 text-sm"><strong>Tip:</strong> {example.tip}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Examples */}
          <section id="examples" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Example Prompts to Try
            </h2>
            <p className="text-slate-600 mb-6">
              Copy these prompts directly into ColorWonder to see great results:
            </p>

            <div className="grid gap-3">
              {[
                "Cute baby panda eating bamboo in a forest with butterflies flying around",
                "Magical unicorn with rainbow mane standing beside a sparkling waterfall",
                "Astronaut floating in space surrounded by friendly cartoon planets with faces",
                "Pirate ship sailing on ocean waves with a treasure chest on deck",
                "Family of owls sitting in a tree under a crescent moon",
                "Dragon reading a book while sitting on a pile of gold coins",
                "Mermaid swimming with dolphin friends in coral reef",
                "Robot helping plant flowers in a garden"
              ].map((prompt, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                  <span className="w-6 h-6 bg-violet-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {i + 1}
                  </span>
                  <p className="text-slate-700">{prompt}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {[
                {
                  q: "Is it really free to create coloring pages?",
                  a: "Yes! ColorWonder offers a free tier with 10 pages per month. You can create, download, and print without paying anything. Paid plans offer more pages and features."
                },
                {
                  q: "Do I need any design skills?",
                  a: "Not at all! The AI does all the design work. You just describe what you want in plain English, and ColorWonder creates it for you."
                },
                {
                  q: "How long does it take to generate a page?",
                  a: "Each page takes about 30-60 seconds to generate. A 5-page coloring book can be ready in under 5 minutes!"
                },
                {
                  q: "Can I use these pages commercially?",
                  a: "The free and Basic plans are for personal use only. Plus and Pro plans include commercial licenses if you want to sell your creations."
                },
                {
                  q: "What if I don't like the result?",
                  a: "You can regenerate any page that doesn't meet your expectations. Just click 'Regenerate' and get a new version with the same prompt."
                },
                {
                  q: "What paper should I print on?",
                  a: "Regular printer paper works great! For best results, use white paper. Card stock can be nice for more durable pages, but isn't required."
                }
              ].map((faq, i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-100 p-5">
                  <h4 className="font-semibold text-slate-800 mb-2">{faq.q}</h4>
                  <p className="text-slate-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Final CTA */}
          <section className="mb-12">
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-8 text-white text-center">
              <Sparkles className="w-10 h-10 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Ready to Create Your First Coloring Page?
              </h3>
              <p className="text-violet-100 mb-6 max-w-md mx-auto">
                Join thousands of parents and teachers creating custom coloring pages. It's free to start!
              </p>
              <Button
                onClick={() => window.location.href = "/signup"}
                className="h-14 px-8 rounded-full bg-white text-violet-600 font-semibold text-lg hover:bg-violet-50"
              >
                Create Your Free Account
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </section>

          {/* Conclusion */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Wrapping Up
            </h2>
            <p className="text-slate-600 mb-4">
              Creating coloring pages with AI is easier than ever. With ColorWonder, you can go from idea to printed page in just a few minutes – no design skills needed.
            </p>
            <p className="text-slate-600">
              The key is writing good prompts that describe what you want. Be specific, include details about the setting and action, and don't be afraid to get creative. Your kids will love having unique coloring pages made just for them!
            </p>
          </section>

          {/* Related Articles */}
          <section className="border-t border-slate-200 pt-12">
            <h3 className="text-xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Related Articles
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/blog/coloring-page-prompts" className="p-4 bg-white rounded-xl border border-slate-100 hover:border-violet-200 transition-colors">
                <h4 className="font-semibold text-slate-800 mb-1">50 Free Coloring Page Prompts</h4>
                <p className="text-slate-500 text-sm">Ready-to-use prompt ideas for kids</p>
              </Link>
              <Link to="/blog/custom-coloring-pages" className="p-4 bg-white rounded-xl border border-slate-100 hover:border-violet-200 transition-colors">
                <h4 className="font-semibold text-slate-800 mb-1">Custom Coloring Pages in Seconds</h4>
                <p className="text-slate-500 text-sm">Make personalized pages for any occasion</p>
              </Link>
            </div>
          </section>
        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8 px-6">
        <div className="max-w-6xl mx-auto text-center text-slate-500 text-sm">
          <p>© 2026 ColorWonder. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ArticleCreateColoringPagesWithAI;
